import { Box, styled } from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { ContentsTable } from '../ContentsTable/ContentsTable';
import { useContextMenu } from '@/hooks/useContextMenu';
import { ApiErrorAlert } from '../Errors/ApiErrorAlert';
import { useInfiniteReportGroups } from '@/hooks/api/reports/useInfiniteReportGroups';
import { REPORT_GROUP_ASSETS_QUERY_KEY, useListReportGroupAssets } from '@/hooks/api/reports/useListReportGroupAssets';
import { useGetReport } from '@/hooks/api/reports/useGetReport';
import { ReportPageObjectModel } from '@/poms/reportPageObjectModel';
import { ReportContextMenu } from './ReportContextMenu';
import { ReportDownload } from './ReportDownload';
import { FolderContent, JobStatus, UUID } from '@/api/generatedApi';
import { ReportZeroState } from './ReportZeroState';
import { ReportBulkActions } from '../ContentsTable/ReportBulkActions';
import { useGetCurrentUser } from '@/hooks/api/users/useGetCurrentUser';
import { useEffect, useState, useCallback, useMemo } from 'react';
import { useTenants } from '@/services/tenants/useTenants';
import { useReportDrag } from './ReportDragContext';
import { reportTableColumns } from './reportTableColumnDefinitions';
import { useGetReportGroupFilters } from '@/hooks/api/reports/useGetReportGroupFilters';
import { useMetadataColumnVisibility } from '@/hooks/useMetadataColumnVisibility';
import { insertMetadataColumnsAfterDetails } from '@/utils/metadata';

const ContentArea = styled(Box)({
  flex: 1,
  minWidth: 0,
  width: '100%',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
});

export const ReportTable = () => {
  const { id, groupId } = useParams();
  const { gridApiReference } = useReportDrag();
  const navigate = useNavigate();
  const { getPath } = useTenants();
  const { permissions } = useGetCurrentUser();
  const queryClient = useQueryClient();
  const { report } = useGetReport({ id });
  const {
    groups,
    isLoading,
    error: groupsError,
    rawResult: { refetch },
  } = useInfiniteReportGroups({ id });

  const handleRefresh = useCallback(() => {
    queryClient.resetQueries({ queryKey: [REPORT_GROUP_ASSETS_QUERY_KEY], exact: false });
  }, [queryClient]);

  const [hasNavigated, setHasNavigated] = useState<boolean>(false);

  useEffect(() => {
    if (!groupId && !hasNavigated && !isLoading && groups.length > 0) {
      navigate(getPath(`report/${id}/${groups[0]?.id}`), { replace: true });
      setHasNavigated(true);
    }
  }, [groupId, hasNavigated, isLoading, groups, navigate, getPath, id]);

  const selectedGroup = groups.find(group => group.id === groupId);
  const assetIdsTotal = selectedGroup?.assetIds.length ?? 0;

  const { fetchData: fetchAssets, error: assetsError } = useListReportGroupAssets({
    reportId: id,
    groupId,
  });

  const { data: reportGroupFiltersData, isLoading: isLoadingReportGroupFilters } = useGetReportGroupFilters({
    reportId: id ?? '',
    groupId: groupId ?? '',
    enabled: !!id && !!groupId,
  });

  const metadataFieldNames = useMemo(
    () => Object.keys(reportGroupFiltersData?.filterData.metadata ?? {}),
    [reportGroupFiltersData?.filterData.metadata]
  );

  const { metadataColumns, columnVisibilityModel, onColumnVisibilityModelChange, isMetadataLoading } =
    useMetadataColumnVisibility({
      metadataFieldNames,
      isLoadingFieldNames: isLoadingReportGroupFilters,
      canEditMetadata: permissions.canModifyAssetMetadata,
      contextKey: `${id || ''}-${groupId || ''}`,
    });

  const canManageReportGroups = permissions.canManageReportGroups;

  const {
    contextMenuPosition,
    contextMenuClickedItem,
    contextMenuClickedField,
    handleGridContextMenu,
    handleCloseContextMenu,
  } = useContextMenu<FolderContent>({
    canUseContextMenu: true,
    getRowById: (rowId: UUID) => gridApiReference.current?.getRow<FolderContent>(rowId) ?? undefined,
  });

  const columns = useMemo(
    () => insertMetadataColumnsAfterDetails(reportTableColumns, metadataColumns),
    [metadataColumns]
  );

  const handleAssetRemoved = () => {
    refetch();
  };

  let content: React.ReactNode;

  if (!isLoading && assetsError?.message === 'Report group not found') {
    content = <ReportZeroState title='This group no longer exists' />;
  } else if (!isLoading && (groupsError || assetsError)) {
    content = <ApiErrorAlert error={groupsError || assetsError} />;
  } else if (!isLoading && groups.length === 0) {
    content = <ReportZeroState title='No Groups Found' description='This report has no groups.' />;
  } else if (!isLoading && selectedGroup && assetIdsTotal === 0) {
    content = (
      <ReportZeroState
        title='No Assets in Group'
        description='This group is empty. Drag-and-drop assets from other groups to add assets.'
      />
    );
  } else {
    content = (
      <>
        <Box sx={{ flex: 1, minHeight: 0, minWidth: 0, width: '100%', overflow: 'hidden' }}>
          <ContentsTable
            enableBulkActions={canManageReportGroups}
            contextMenuOptions={[]}
            enableRowDragging={true}
            fetchContents={fetchAssets}
            isLoading={!groupId || isLoading}
            onRowContextMenu={canManageReportGroups ? handleGridContextMenu : undefined}
            gridApiRef={gridApiReference}
            customBaseColumns={reportTableColumns}
            metadataColumns={metadataColumns}
            columnVisibilityModel={columnVisibilityModel}
            onColumnVisibilityModelChange={onColumnVisibilityModelChange}
            isColumnDataLoading={isMetadataLoading}
            showCreateButton={false}
            showFiltersButton={false}
            onRefresh={handleRefresh}
            toolbarActions={report?.status === JobStatus.COMPLETED && id ? <ReportDownload id={id} /> : undefined}
            bulkActions={
              canManageReportGroups && id && groupId ? (
                <ReportBulkActions reportId={id} groupId={groupId} assetCount={assetIdsTotal} />
              ) : undefined
            }
          />
        </Box>
        {id && selectedGroup && (
          <ReportContextMenu
            anchorPosition={contextMenuPosition}
            onClose={handleCloseContextMenu}
            clickedItem={contextMenuClickedItem}
            clickedField={contextMenuClickedField}
            columns={columns}
            reportId={id}
            groupId={selectedGroup.id}
            onRemoved={handleAssetRemoved}
            assetCount={assetIdsTotal}
            canManageGroups={canManageReportGroups}
          />
        )}
      </>
    );
  }

  return <ContentArea data-testid={ReportPageObjectModel.ReportTable}>{content}</ContentArea>;
};
